import { Injectable, inject, signal, computed } from '@angular/core';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { environment } from '../../../environments/environment';
import { hasText } from '../strings';

const authConfig: AuthConfig = {
  issuer:        environment.auth.issuer,
  clientId:      environment.auth.clientId,
  redirectUri:   environment.auth.redirectUri,
  responseType:  'code',
  scope:         environment.auth.scope,
  // Cognito's issuer ≠ hosted-UI domain, so we set endpoints explicitly
  loginUrl:      `${environment.auth.hostedUiBase}/oauth2/authorize`,
  tokenEndpoint: `${environment.auth.hostedUiBase}/oauth2/token`,
  logoutUrl:     `${environment.auth.hostedUiBase}/logout`,
  requireHttps:         environment.production,
  showDebugInformation: !environment.production,
  skipIssuerCheck: true,
  strictDiscoveryDocumentValidation: false,
  clearHashAfterLogin: false,
};

/**
 * One claim as text, or `undefined` when it is absent or is neither a string nor a number.
 *
 * `getIdentityClaims()` is typed `object` by the library, so its values are `unknown` — this is the
 * one place that narrows them. Cognito sends every claim as a string, including `is_system_admin`,
 * which is why the check below compares against `'True'`.
 *
 * **Numbers are accepted deliberately.** The reads used to go through `any`, so a numeric `user_id`
 * would have been passed to `Number()` and worked; narrowing to strings alone would have quietly
 * turned that into "no id". Anything else — an object, an array, a boolean — is treated as absent
 * rather than coerced into a plausible-looking value.
 */
function claimText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  return typeof value === 'number' ? String(value) : undefined;
}

export interface VestfrostClaims {
  userId:         number | null;
  organizationId: number | null;
  isSystemAdmin:  boolean;
  email:          string;
  name:           string;
  initials:       string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private oauth = inject(OAuthService);

  private _ready = signal(false);
  readonly ready = this._ready.asReadonly();

  constructor() {
    this.oauth.configure(authConfig);
  }

  async init(): Promise<void> {
    await this.oauth.loadDiscoveryDocument(
      `${environment.auth.issuer}/.well-known/openid-configuration`
    );
    await this.oauth.tryLoginCodeFlow();
    if (!this.isAuthenticated) {
      this.login();
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise<void>(() => {});
    }
    this._ready.set(true);
  }

  get isAuthenticated(): boolean {
    return this.oauth.hasValidIdToken();
  }

  get idToken(): string {
    return this.oauth.getIdToken();
  }

  readonly claims = computed<VestfrostClaims>(() => {
    this._ready();
    const raw = this.oauth.getIdentityClaims() as Record<string, unknown> | null;
    if (!raw) {
      return { userId: null, organizationId: null, isSystemAdmin: false, email: '', name: '', initials: '' };
    }
    const name: string = claimText(raw['name']) ?? claimText(raw['email']) ?? '';
    return {
      // `hasText` rather than a truthy check: Cognito sends every claim as a string, and `Number('')`
      // is `0` — a blank claim would otherwise become a real-looking id rather than "no id".
      userId:         hasText(claimText(raw['user_id']))         ? Number(raw['user_id'])         : null,
      organizationId: hasText(claimText(raw['organization_id'])) ? Number(raw['organization_id']) : null,
      isSystemAdmin:  raw['is_system_admin'] === 'True' || raw['is_system_admin'] === true,
      email:          claimText(raw['email']) ?? '',
      name,
      initials:       name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2),
    };
  });

  login(): void {
    this.oauth.initCodeFlow();
  }

  logout(): void {
    this.oauth.logOut(true); // clear local tokens without built-in redirect
    const params = new URLSearchParams({
      client_id:  environment.auth.clientId,
      logout_uri: environment.auth.redirectUri,
    });
    window.location.href = `${environment.auth.hostedUiBase}/logout?${params}`;
  }
}
